/**
 * Layout Viewer
 * Interactive display of layout components (Stack, Grid, Container, Section)
 */

import React, { useState } from "react";
import { Box, Heading, Button, Divider, SimpleGrid, Grid, GridItem, Card, CardBody } from "@chakra-ui/react";
import CDS, { useResolveColor } from "design-system";

function StackExamples() {
  const exampleBg = useResolveColor(CDS.Color.surface.secondary);

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Stack Component</Heading>

      <SimpleGrid columns={1} spacing={6}>
        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                <CDS.Text14 fontWeight="semibold">Vertical Stack</CDS.Text14>
                <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>
                  {'direction="vertical"'}
                </CDS.Tag>
              </CDS.Stack>
              <Box p={4} bg={exampleBg} borderRadius="md">
                <CDS.Stack spacing={CDS.Spacing.medium}>
                  <Box p={3} bg="blue.100" borderRadius="md" w="full" textAlign="center">
                    Item 1
                  </Box>
                  <Box p={3} bg="blue.100" borderRadius="md" w="full" textAlign="center">
                    Item 2
                  </Box>
                  <Box p={3} bg="blue.100" borderRadius="md" w="full" textAlign="center">
                    Item 3
                  </Box>
                </CDS.Stack>
              </Box>
              <CDS.Text12 color="gray.600" fontFamily="mono">
                {'<CDS.Stack direction="vertical" spacing={CDS.Spacing.medium()}>'}
              </CDS.Text12>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                <CDS.Text14 fontWeight="semibold">Horizontal Stack</CDS.Text14>
                <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>
                  {'direction="horizontal"'}
                </CDS.Tag>
              </CDS.Stack>
              <Box p={4} bg={exampleBg} borderRadius="md">
                <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.medium}>
                  <Box p={3} bg="green.100" borderRadius="md" flex={1} textAlign="center">
                    Item 1
                  </Box>
                  <Box p={3} bg="green.100" borderRadius="md" flex={1} textAlign="center">
                    Item 2
                  </Box>
                  <Box p={3} bg="green.100" borderRadius="md" flex={1} textAlign="center">
                    Item 3
                  </Box>
                </CDS.Stack>
              </Box>
              <CDS.Text12 color="gray.600" fontFamily="mono">
                {'<CDS.Stack direction="horizontal" spacing={CDS.Spacing.large()}>'}
              </CDS.Text12>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                <CDS.Text14 fontWeight="semibold">Stack with Alignment</CDS.Text14>
                <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>
                  {'align="center" justify="between"'}
                </CDS.Tag>
              </CDS.Stack>
              <Box p={4} bg={exampleBg} borderRadius="md">
                <CDS.Stack
                  direction={CDS.Stack.direction.horizontal}
                  justify={CDS.Stack.justify.between}
                  align={CDS.Stack.align.center}
                  w="full"
                >
                  <Box p={2} bg="purple.100" borderRadius="md" fontSize="sm">
                    Small
                  </Box>
                  <Box p={4} bg="purple.100" borderRadius="md">
                    Medium
                  </Box>
                  <Box p={6} bg="purple.100" borderRadius="md" fontSize="lg">
                    Large
                  </Box>
                </CDS.Stack>
              </Box>
              <CDS.Text12 color="gray.600" fontFamily="mono">
                {'<CDS.Stack direction="horizontal" align={CDS.Stack.align.center} justify="between">'}
              </CDS.Text12>
            </CDS.Stack>
          </CardBody>
        </Card>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function GridExamples() {
  const exampleBg = useResolveColor(CDS.Color.surface.secondary);

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Grid Component</Heading>

      <SimpleGrid columns={1} spacing={6}>
        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                <CDS.Text14 fontWeight="semibold">Simple Grid</CDS.Text14>
                <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>
                  columns=
                  {3}
                </CDS.Tag>
              </CDS.Stack>
              <Box p={4} bg={exampleBg} borderRadius="md">
                <SimpleGrid columns={3} spacing={CDS.Spacing.medium}>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Box
                      key={i}
                      p={4}
                      bg="blue.100"
                      borderRadius="md"
                      textAlign="center"
                      minH="60px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      Item {i + 1}
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
              <CDS.Text12 color="gray.600" fontFamily="mono">
                {"<CDS.Grid columns={3} gap={CDS.Spacing.medium()}>"}
              </CDS.Text12>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                <CDS.Text14 fontWeight="semibold">Responsive Grid</CDS.Text14>
                <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>
                  responsive columns
                </CDS.Tag>
              </CDS.Stack>
              <Box p={4} bg={exampleBg} borderRadius="md">
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={CDS.Spacing.medium}>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Box
                      key={i}
                      p={4}
                      bg="green.100"
                      borderRadius="md"
                      textAlign="center"
                      minH="80px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      Card {i + 1}
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
              <CDS.Text12 color="gray.600" fontFamily="mono">
                {"<CDS.Grid columns={{ base: 1, md: 2, lg: 4 }}>"}
              </CDS.Text12>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between}>
                <CDS.Text14 fontWeight="semibold">Complex Layout Grid</CDS.Text14>
                <CDS.Tag size={CDS.Tag.size.small} variant={CDS.Tag.variant.neutral}>
                  custom template
                </CDS.Tag>
              </CDS.Stack>
              <Box p={4} bg={exampleBg} borderRadius="md">
                <Grid templateColumns="1fr 300px" templateRows="auto 1fr auto" gap={4} minH="200px">
                  <GridItem colSpan={2} bg="purple.100" borderRadius="md" p={4}>
                    Header
                  </GridItem>
                  <GridItem bg="purple.100" borderRadius="md" p={4}>
                    Main Content
                  </GridItem>
                  <GridItem bg="purple.100" borderRadius="md" p={4}>
                    Sidebar
                  </GridItem>
                  <GridItem colSpan={2} bg="purple.100" borderRadius="md" p={4}>
                    Footer
                  </GridItem>
                </Grid>
              </Box>
              <CDS.Text12 color="gray.600" fontFamily="mono">
                {'<CDS.Grid templateColumns="1fr 300px" templateRows="auto 1fr auto">'}
              </CDS.Text12>
            </CDS.Stack>
          </CardBody>
        </Card>
      </SimpleGrid>
    </CDS.Stack>
  );
}

function ContainerExamples() {
  const exampleBg = useResolveColor(CDS.Color.surface.secondary);

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Container Component</Heading>

      <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
        <CDS.Text12 color="gray.600">
          Containers provide consistent max-width and centering for page content.
        </CDS.Text12>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <CDS.Text14 fontWeight="semibold">Container Sizes</CDS.Text14>

              <Box bg={exampleBg} p={4} borderRadius="md">
                <CDS.Stack spacing={CDS.Spacing.medium}>
                  <Box w="full" maxW="sm" bg="blue.100" p={3} borderRadius="md" textAlign="center">
                    Small Container (max-width: 24rem)
                  </Box>
                  <Box w="full" maxW="md" bg="green.100" p={3} borderRadius="md" textAlign="center">
                    Medium Container (max-width: 28rem)
                  </Box>
                  <Box w="full" maxW="lg" bg="purple.100" p={3} borderRadius="md" textAlign="center">
                    Large Container (max-width: 32rem)
                  </Box>
                  <Box w="full" maxW="xl" bg="orange.100" p={3} borderRadius="md" textAlign="center">
                    Extra Large Container (max-width: 36rem)
                  </Box>
                </CDS.Stack>
              </Box>

              <CDS.Text12 color="gray.600" fontFamily="mono">
                {"<CDS.Container size={CDS.containerSize.large()}>"}
              </CDS.Text12>
            </CDS.Stack>
          </CardBody>
        </Card>
      </CDS.Stack>
    </CDS.Stack>
  );
}

function SectionExamples() {
  const exampleBg = useResolveColor(CDS.Color.surface.secondary);

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Section Component</Heading>

      <Card>
        <CardBody>
          <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
            <CDS.Text14 fontWeight="semibold">Semantic Sections</CDS.Text14>

            <Box bg={exampleBg} p={4} borderRadius="md">
              <CDS.Stack spacing={CDS.Spacing.medium} align={CDS.Stack.align.stretch}>
                <Box as="header" bg="blue.100" p={4} borderRadius="md">
                  <CDS.Text14 fontWeight="semibold">Header Section</CDS.Text14>
                  <CDS.Text12 color="gray.600">
                    {'as="header"'}
                  </CDS.Text12>
                </Box>

                <Box as="main" bg="green.100" p={4} borderRadius="md">
                  <CDS.Text14 fontWeight="semibold">Main Content Section</CDS.Text14>
                  <CDS.Text12 color="gray.600">
                    {'as="main"'}
                  </CDS.Text12>
                </Box>

                <Box as="aside" bg="purple.100" p={4} borderRadius="md">
                  <CDS.Text14 fontWeight="semibold">Sidebar Section</CDS.Text14>
                  <CDS.Text12 color="gray.600">
                    {'as="aside"'}
                  </CDS.Text12>
                </Box>

                <Box as="footer" bg="orange.100" p={4} borderRadius="md">
                  <CDS.Text14 fontWeight="semibold">Footer Section</CDS.Text14>
                  <CDS.Text12 color="gray.600">
                    {'as="footer"'}
                  </CDS.Text12>
                </Box>
              </CDS.Stack>
            </Box>

            <CDS.Text12 color="gray.600" fontFamily="mono">
              {'<CDS.Section as="main" padding={CDS.Spacing.large()}>'}
            </CDS.Text12>
          </CDS.Stack>
        </CardBody>
      </Card>
    </CDS.Stack>
  );
}

export function LayoutViewer() {
  const [activeTab, setActiveTab] = useState("stack");

  const tabs = [
    { id: "stack", name: "Stack", component: StackExamples },
    { id: "grid", name: "Grid", component: GridExamples },
    { id: "container", name: "Container", component: ContainerExamples },
    { id: "section", name: "Section", component: SectionExamples },
  ];

  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            size="sm"
            variant={activeTab === tab.id ? "solid" : "ghost"}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.name}
          </Button>
        ))}
      </CDS.Stack>

      <Divider />

      <CDS.Box>{ActiveComponent && <ActiveComponent />}</CDS.Box>
    </CDS.Stack>
  );
}
